import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/integrations/supabase/client";

export default function GoogleCalendarCallbackPage() {
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState<string>("Completing connection…");

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    const getRedirectUriFromState = (): string => {
      if (!state) return `${window.location.origin}/user-settings`;
      try {
        const once = decodeURIComponent(state);
        let parsed: { redirect_uri?: string };
        try {
          parsed = JSON.parse(once);
        } catch {
          parsed = JSON.parse(decodeURIComponent(once));
        }
        if (parsed?.redirect_uri) return parsed.redirect_uri.replace(/\?.*$/, "");
      } catch {
        // ignore
      }
      return `${window.location.origin}/user-settings`;
    };

    const redirectTo = getRedirectUriFromState();

    if (errorParam) {
      window.location.replace(`${redirectTo}?error=${encodeURIComponent(errorParam)}`);
      return;
    }

    if (!code || !state) {
      setMessage("Missing code or state. Try connecting again from settings.");
      return;
    }

    (async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        window.location.replace(`${redirectTo}?error=${encodeURIComponent("Session expired. Sign in again and try connecting.")}`);
        return;
      }

      setMessage("Saving calendar connection…");
      const res = await fetch(`${SUPABASE_URL}/functions/v1/google-calendar-oauth-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          code,
          state,
          access_token: session.access_token,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; success?: boolean; redirect_uri?: string; email?: string };

      if (!res.ok || data?.error) {
        const err = data?.error || "Failed to connect calendar";
        window.location.replace(`${redirectTo}?error=${encodeURIComponent(err)}`);
        return;
      }

      const finalRedirect = data?.redirect_uri || redirectTo;
      const email = data?.email ?? "";
      window.location.replace(`${finalRedirect}?success=calendar_connected&email=${encodeURIComponent(email)}`);
    })();
  }, [searchParams]);

  return (
    <div className="flex min-h-[40vh] items-center justify-center p-4">
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}
